// src/components/heatmaps/AnalysisViewer.jsx

import { useState, useEffect, useRef, useContext } from "react";
import styles from "../../css/heatmaps/AnalysisViewer.module.css";
import ButtonBtn from "../buttons/ButtonBtn";
import { ReactSVG } from "react-svg";
import wandIcon from "../../img/icons/wand.svg";
import downloadIcon from "../../img/icons/download.svg";
import JSZip from "jszip";
// import Modal from "../Modal";
import ModalLoading from "../ModalLoading";
import axiosInstance from "../../api/AxiosConfig";
import AIPurposeModal from "./AIPurposeModal";
import { getAiTypeDescription } from "./aiPurposeOptions";
import HeatmapPreviewModal from "./HeatmapPreviewModal";
import { AppContext } from "../../context/AppContext";
import {
  downloadHeatmapAsPdf,
  generateHeatmapPdf,
} from "../../utils/generateHeatmapPdf";
import { ReactComponent as HelpIcon } from "../../img/icons/help.svg";
import HintBox from "../HintBox";

// Požadavek: prohodit pořadí tlačítek – nejdřív Heatmapa, pak Originál
// Rozšíření: Každý typ má i text pro tlačítko (skloňování apod.)
const IMAGE_TYPES = {
  heatmap: { label: "Heatmapa", button: "heatmapu" },
  original: { label: "Originál", button: "originál" },
};

const AnalysisViewer = ({ analysisData, onAiAnalysisCompleted }) => {
  const { userData, setUserData, setShowModalCredit } = useContext(AppContext);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("heatmap");
  const [isMagicOpen, setIsMagicOpen] = useState(false);
  const [magicType, setMagicType] = useState("Brand");
  const [isMagicSubmitting, setIsMagicSubmitting] = useState(false);
  const [aiMessagesByKey, setAiMessagesByKey] = useState({}); // { [stableKey]: markdown bez radku se skore }
  const [aiScoresByKey, setAiScoresByKey] = useState({}); // { [stableKey]: { val, max } }
  const analysisRef = useRef(null);
  const [scrollToAnalysisKey, setScrollToAnalysisKey] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // --- LocalStorage helpers for persistence across route changes
  const STORAGE_PREFIX = "permoniq_ai_analysis:";
  const storageKeyFor = (key) => `${STORAGE_PREFIX}${key}`;
  const saveAnalysisToStorage = (key, payload) => {
    try {
      const data = {
        payload: payload?.payload ?? payload?.markdown ?? "",
        score: payload?.score || null,
        updatedAt: Date.now(),
      };
      localStorage.setItem(storageKeyFor(key), JSON.stringify(data));
    } catch (e) {
      // best effort only
      console.warn("AI analysis localStorage save failed", e);
    }
  };
  const loadAnalysisFromStorage = (key) => {
    try {
      const raw = localStorage.getItem(storageKeyFor(key));
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  };

  // Stabilní klíč pro mapování AI zpráv a skóre
  const getStableKey = (result, index) =>
    result?.heatmap_id || result?.image_id || String(index);

  useEffect(() => {
    // Reset selection when new analysis set is provided
    setActiveIndex(0);
    setActiveTab("heatmap");

    // Hydrate AI messages and scores from props (analysis_result) and localStorage
    if (!Array.isArray(analysisData) || analysisData.length === 0) return;
    const nextMsgs = {};
    const nextScores = {};
    analysisData.forEach((item, idx) => {
      const key = getStableKey(item, idx);
      // Prefer what we already have in state
      if (aiMessagesByKey[key]) {
        nextMsgs[key] = aiMessagesByKey[key];
        if (aiScoresByKey[key]) nextScores[key] = aiScoresByKey[key];
        return;
      }
      // If backend provided analysis_result on this item, use it
      if (item?.analysis_result) {
        const payload = extractPayload(item.analysis_result);
        const { scoreVal, scoreMax } = parseScore(payload);
        if (payload != null) nextMsgs[key] = payload;
        if (typeof scoreVal === "number" && typeof scoreMax === "number") {
          nextScores[key] = { val: scoreVal, max: scoreMax };
        }
        // persist for future mounts
        saveAnalysisToStorage(key, {
          payload,
          score:
            typeof scoreVal === "number" && typeof scoreMax === "number"
              ? { val: scoreVal, max: scoreMax }
              : null,
        });
        return;
      }
      // Otherwise, try localStorage
      const stored = loadAnalysisFromStorage(key);
      const storedPayload = stored?.payload ?? stored?.markdown;
      if (storedPayload) {
        nextMsgs[key] = storedPayload;
        if (
          stored.score &&
          typeof stored.score.val === "number" &&
          typeof stored.score.max === "number"
        ) {
          nextScores[key] = stored.score;
        }
      }
    });
    if (Object.keys(nextMsgs).length > 0)
      setAiMessagesByKey((prev) => ({ ...prev, ...nextMsgs }));
    if (Object.keys(nextScores).length > 0)
      setAiScoresByKey((prev) => ({ ...prev, ...nextScores }));
    // Intentionally not depending on aiMessagesByKey/aiScoresByKey to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisData]);

  // Připrav aktivní záznam kvůli hookům níže (bezpečný přístup při nulových datech)
  const activeResult = analysisData?.[activeIndex];

  // Po zapsání AI zprávy do stavu a při aktivní heatmapě jemně sjeď na box s analýzou
  useEffect(() => {
    if (!activeResult) return;
    const key = getStableKey(activeResult, activeIndex);
    if (scrollToAnalysisKey !== key) return;
    if (activeTab !== "heatmap") return;
    const hasMsg = Boolean(
      aiMessagesByKey[key] ||
      activeResult?.analysis_result ||
      loadAnalysisFromStorage(key)?.payload ||
      loadAnalysisFromStorage(key)?.markdown,
    );
    if (!hasMsg) return;
    // počkej na dom update a scrollni
    const id = setTimeout(() => {
      analysisRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
    // vyčisti flag, ať se scroll nespouští opakovaně
    setScrollToAnalysisKey(null);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiMessagesByKey, activeIndex, activeTab, activeResult]);

  if (!analysisData || analysisData.length === 0) {
    return (
      <div className={styles.placeholder}>
        Nebyly analyzovány žádné obrázky. Nahrajte je v sekci výše.
      </div>
    );
  }

  const handleSelectImage = (index) => {
    setActiveIndex(index);
    setActiveTab("heatmap");
  };

  const getActiveImageData = () => {
    const prefix = "data:image/png;base64,";
    if (!activeResult) return "";
    switch (activeTab) {
      case "heatmap":
        // Map Heatmap view to show overlay image data as requested; fallback to heatmap if overlay missing
        return `${prefix}${
          activeResult.overlay_image_base64 || activeResult.heatmap_image_base64
        }`;
      default:
        return `${prefix}${activeResult.original_image_base64}`;
    }
  };

  const handleDownload = async () => {
    // Check if AI analysis exists for this item
    const key = getStableKey(activeResult, activeIndex);
    const aiCandidate =
      aiMessagesByKey[key] ||
      activeResult?.analysis_result ||
      loadAnalysisFromStorage(key)?.payload ||
      loadAnalysisFromStorage(key)?.markdown;
    const aiParsed = aiCandidate ? extractPayload(aiCandidate) : null;
    const hasAi = isValidAnalysisPayload(aiParsed);

    if (hasAi) {
      // Download as PDF with heatmap + AI analysis
      const scoreData = aiScoresByKey[key] || null;
      const score =
        scoreData &&
        typeof scoreData.val === "number" &&
        typeof scoreData.max === "number"
          ? scoreData
          : (() => {
              const { scoreVal, scoreMax } = parseScore(aiParsed);
              return typeof scoreVal === "number" &&
                typeof scoreMax === "number"
                ? { val: scoreVal, max: scoreMax }
                : null;
            })();
      await downloadHeatmapAsPdf(activeResult, aiParsed, score);
    } else {
      // Fallback: download as plain PNG
      const link = document.createElement("a");
      link.href = getActiveImageData();
      const originalName = activeResult.image_id
        .split(".")
        .slice(0, -1)
        .join(".");
      link.download = `${activeTab}_${originalName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleBulkDownload = async () => {
    if (!Array.isArray(analysisData) || analysisData.length === 0) return;
    try {
      const zip = new JSZip();
      const folder = zip.folder("heatmaps");

      for (let idx = 0; idx < analysisData.length; idx++) {
        const item = analysisData[idx];
        if (!item) continue;
        const baseName = (item.image_id || "obrazek")
          .toString()
          .replace(/\s+/g, "_")
          .replace(/[^a-zA-Z0-9_\.-]/g, "");

        // Check if AI analysis exists for this item
        const key = getStableKey(item, idx);
        const aiCandidate =
          aiMessagesByKey[key] ||
          item?.analysis_result ||
          loadAnalysisFromStorage(key)?.payload ||
          loadAnalysisFromStorage(key)?.markdown;
        const aiParsed = aiCandidate ? extractPayload(aiCandidate) : null;
        const hasAi = isValidAnalysisPayload(aiParsed);

        if (hasAi) {
          // Generate PDF blob with heatmap + AI analysis
          const scoreData = aiScoresByKey[key] || null;
          const score =
            scoreData &&
            typeof scoreData.val === "number" &&
            typeof scoreData.max === "number"
              ? scoreData
              : (() => {
                  const { scoreVal, scoreMax } = parseScore(aiParsed);
                  return typeof scoreVal === "number" &&
                    typeof scoreMax === "number"
                    ? { val: scoreVal, max: scoreMax }
                    : null;
                })();
          const imageBase64 =
            item.overlay_image_base64 || item.heatmap_image_base64 || "";
          const pdfBlob = await generateHeatmapPdf({
            imageBase64,
            imageName: item.image_id || "heatmapa",
            analysisPayload: aiParsed,
            score,
            fileName: `heatmap_${baseName}`,
            asBlob: true,
          });
          if (pdfBlob) {
            folder.file(`${baseName}_heatmap_report.pdf`, pdfBlob);
          }
        } else {
          const heatmapData =
            item.overlay_image_base64 || item.heatmap_image_base64;
          if (heatmapData)
            folder.file(`${baseName}_heatmap.png`, heatmapData, {
              base64: true,
            });
        }
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `heatmaps_${new Date()
        .toISOString()
        .replace(/[:T]/g, "-")
        .slice(0, 19)}.zip`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
      }, 1000);
    } catch (e) {
      console.error("Bulk download failed", e);
    }
  };

  const handleOpenPreview = () => {
    setIsPreviewOpen(true);
  };

  const PROMPT_MAP = {
    Brand:
      "Banner by měl posílit povědomí o značce a vizuálně prezentovat brand (brandový účel).",
    Akviziční: "Banner by měl zaujmout a přilákat zákazníka (akviziční účet).",
    Konverzní:
      "Banner by měl motivovat uživatele k provedení konkrétní akce, například k nákupu, registraci nebo odeslání formuláře (konverzní účel).",
    "Služba/nástroj":
      "Banner by měl prezentovat konkrétní službu nebo nástroj a povzbudit uživatele k jeho vyzkoušení nebo použití (účel: služba/nástroj).",
  };

  const extractPayload = (data) => {
    if (data == null) return "";
    if (typeof data === "string") {
      // Try to parse JSON from a string (supports optional ```json fences)
      try {
        let t = data.trim();
        // strip code fences if present
        if (t.startsWith("```")) {
          t = t.replace(/^```(?:json|javascript)?\n?/i, "");
          t = t.replace(/```\s*$/i, "");
          t = t.trim();
        }
        if (
          (t.startsWith("{") && t.endsWith("}")) ||
          (t.startsWith("[") && t.endsWith("]"))
        ) {
          const parsed = JSON.parse(t);
          return parsed;
        }
      } catch (e) {
        // fall through to return original string
      }
      return data;
    }
    if (Array.isArray(data)) return data;
    if (typeof data === "object") {
      // Preserve explicit error objects so the UI can render a friendly message
      if (data._error === true || data.error === true) return data;
      if (
        data.typ ||
        data.analyza ||
        data.doporuceni ||
        data.hodnoceni ||
        data.permoniq_doporuceni
      )
        return data;
      const candidate =
        data.analysis_result ||
        data.message ||
        data.result ||
        data.detail ||
        data.analysis ||
        data.output ||
        "";
      // Recurse to allow parsing strings with JSON inside (including code fences)
      return extractPayload(candidate);
    }
    return "";
  };

  // Validate payload has expected JSON structure (object/array with known keys)
  const isValidAnalysisPayload = (payload) => {
    if (!payload) return false;
    const isValidObj = (o) =>
      o &&
      typeof o === "object" &&
      (o.typ ||
        o.analyza ||
        o.doporuceni ||
        o.hodnoceni ||
        o.permoniq_doporuceni);
    if (Array.isArray(payload)) return payload.some((o) => isValidObj(o));
    return isValidObj(payload);
  };

  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  // Request analysis with retry until we get valid JSON payload or hit max attempts
  const requestAnalysisWithRetry = async (
    analysisId,
    prompt,
    maxAttempts = 10,
  ) => {
    let lastRaw = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const resp = await axiosInstance.post(
          `analyze_heatmaps/${encodeURIComponent(analysisId)}/`,
          { user_prompt: prompt },
        );
        const raw = extractPayload(resp?.data);
        lastRaw = raw ?? "";
        if (isValidAnalysisPayload(raw)) {
          return { ok: true, payload: raw };
        }
      } catch (e) {
        // Keep lastRaw as error message if nothing else
        lastRaw =
          e?.response?.data?.message ||
          e?.message ||
          "Nepodařilo se analyzovat.";
      }
      // small backoff to avoid hammering
      await sleep(Math.min(300 * attempt, 1500));
    }
    return { ok: false, payload: lastRaw };
  };

  const renderAnalysisContent = (payload) => {
    if (!payload) return null;
    // Friendly error message with a clickable e‑mail when backend request fails
    if (
      (typeof payload === "object" &&
        (payload._error === true || payload.error === true)) ||
      (typeof payload === "string" &&
        /(error|chyba|failed|status\s*code|4\d{2}|5\d{2})/i.test(payload))
    ) {
      const email =
        (typeof payload === "object" && payload.email) || "info@permoniq.com";
      return (
        <div className={styles.aiBlock}>
          <p>
            Při pokusu o AI analýzu došlo k chybě. Zkuste to prosím znovu
            později.
          </p>
          <p>
            Pokud se vám během tohoto pokusu odečetl kredit, napište nám na{" "}
            <a
              className="link"
              href={`mailto:${email}`}
              rel="noopener noreferrer"
              style={{ color: "inherit" }}
            >
              {email}
            </a>
            .
          </p>
        </div>
      );
    }
    const renderList = (pairs) => (
      <ul className={styles.aiList}>
        {pairs.map(([label, value], i) =>
          value ? (
            <li key={i}>
              <strong>{label}:</strong> {value}
            </li>
          ) : null,
        )}
      </ul>
    );
    const Block = ({ obj, idx }) => {
      if (!obj || typeof obj !== "object") return null;
      const analysisPairs = [];
      if (obj.analyza && typeof obj.analyza === "object") {
        const a = obj.analyza;
        analysisPairs.push(["Logo", a.logo]);
        analysisPairs.push(["Claim", a.claim]);
        analysisPairs.push(["CTA", a.cta]);
        analysisPairs.push(["Podklad", a.podklad]);
      }
      const recPairs = [];
      if (obj.doporuceni && typeof obj.doporuceni === "object") {
        const d = obj.doporuceni;
        recPairs.push(["Logo", d.logo]);
        recPairs.push(["Claim", d.claim]);
        recPairs.push(["CTA", d.cta]);
        recPairs.push(["Podklad", d.podklad]);
      }
      return (
        <div className={styles.aiBlock} key={idx}>
          {obj.typ ? (
            <h2 className={styles.aiBlockTitle}>
              {getAiTypeDescription(obj.typ) || String(obj.typ)}
            </h2>
          ) : null}
          {analysisPairs.length > 0 && (
            <section className={styles.aiSection}>
              <h3 className={styles.aiSectionTitle}>Analýza</h3>
              {renderList(analysisPairs)}
            </section>
          )}
          {recPairs.length > 0 && (
            <section className={styles.aiSection}>
              <h3 className={styles.aiSectionTitle}>Doporučení</h3>
              {renderList(recPairs)}
            </section>
          )}
          {obj.hodnoceni ? (
            <p>
              <strong>Celkové hodnocení:</strong> {String(obj.hodnoceni)}
            </p>
          ) : null}
          {obj.permoniq_doporuceni ? (
            <section className={styles.aiSection}>
              <h3 className={styles.aiSectionTitle}>Doporučení PermoniQ</h3>
              <p style={{ whiteSpace: "pre-wrap" }}>
                {String(obj.permoniq_doporuceni)}
              </p>
            </section>
          ) : null}
        </div>
      );
    };
    if (Array.isArray(payload)) {
      return payload.map((o, i) => <Block obj={o} idx={i} key={i} />);
    }
    if (typeof payload === "object") {
      return <Block obj={payload} />;
    }
    return <p style={{ whiteSpace: "pre-wrap" }}>{String(payload)}</p>;
  };

  const stringifyAnalysis = (payload) => {
    try {
      if (!payload) return "";
      if (typeof payload === "string") return payload;
      const lines = [];
      const addBlock = (obj) => {
        if (!obj || typeof obj !== "object") return;
        if (obj.typ) lines.push(`Typ: ${obj.typ}`);
        if (obj.analyza) {
          const a = obj.analyza;
          lines.push("Analýza:");
          if (a.logo) lines.push(`- Logo: ${a.logo}`);
          if (a.claim) lines.push(`- Claim: ${a.claim}`);
          if (a.cta) lines.push(`- CTA: ${a.cta}`);
          if (a.podklad) lines.push(`- Podklad: ${a.podklad}`);
        }
        if (obj.doporuceni) {
          const d = obj.doporuceni;
          lines.push("Doporučení:");
          if (d.logo) lines.push(`- Logo: ${d.logo}`);
          if (d.claim) lines.push(`- Claim: ${d.claim}`);
          if (d.cta) lines.push(`- CTA: ${d.cta}`);
          if (d.podklad) lines.push(`- Podklad: ${d.podklad}`);
        }
        if (obj.hodnoceni) lines.push(`Celkové hodnocení: ${obj.hodnoceni}`);
        if (obj.permoniq_doporuceni) {
          lines.push("Doporučení PermoniQ:");
          lines.push(String(obj.permoniq_doporuceni));
        }
      };
      if (Array.isArray(payload)) {
        payload.forEach((o, i) => {
          if (i > 0) lines.push("---");
          addBlock(o);
        });
        return lines.join("\n");
      }
      addBlock(payload);
      return lines.join("\n");
    } catch (e) {
      return "";
    }
  };

  // Vyparsuje celkové hodnocení (val/max) bez zásahu do textu; toleruje **/__ a různé lomítko
  const parseScore = (input) => {
    // Accepts Markdown string or new JSON schema with hodnoceni: "7/10"
    if (!input) return { scoreVal: null, scoreMax: null };
    if (Array.isArray(input)) {
      for (const it of input) {
        const r = parseScore(it);
        if (typeof r.scoreVal === "number" && typeof r.scoreMax === "number")
          return r;
      }
      return { scoreVal: null, scoreMax: null };
    }
    if (typeof input === "object") {
      const raw = input.hodnoceni || input.score || input.scores || null;
      if (typeof raw === "number" && isFinite(raw)) {
        const val = Math.max(0, Math.min(999, Math.trunc(raw)));
        return { scoreVal: val, scoreMax: 10 };
      }
      if (typeof raw === "string") {
        // Prefer explicit "Celkové hodnocení: X[/Y]?"
        let m = raw.match(
          /Celkové\s+hodnocení[^:]*:\s*(?:\*\*|__)?\s*(\d{1,3})(?:\s*[\/⁄]\s*(\d{1,3}))?/i,
        );
        if (m) {
          const val = parseInt(m[1], 10);
          const max = m[2] ? parseInt(m[2], 10) : 10;
          if (!isNaN(val) && !isNaN(max) && max > 0)
            return { scoreVal: val, scoreMax: max };
        }
        // Generic X/Y
        m = raw.match(/\b(\d{1,3})\s*[\/⁄]\s*(\d{1,3})\b/);
        if (m) {
          const val = parseInt(m[1], 10);
          const max = parseInt(m[2], 10) || 10;
          if (!isNaN(val) && !isNaN(max) && max > 0)
            return { scoreVal: val, scoreMax: max };
        }
        // Single number -> /10
        m = raw.match(/^\s*(\d{1,3})\s*$/);
        if (m) {
          const val = parseInt(m[1], 10);
          if (!isNaN(val)) return { scoreVal: val, scoreMax: 10 };
        }
      }
      if (typeof input.analysis_result === "string")
        return parseScore(input.analysis_result);
      return { scoreVal: null, scoreMax: null };
    }
    if (typeof input === "string") {
      // Prefer explicit line with optional /Y
      let m =
        input.match(
          /Celkové\s+hodnocení[^:]*:\s*(?:\*\*|__)?\s*(\d{1,3})(?:\s*[\/⁄]\s*(\d{1,3}))?/i,
        ) || input.match(/\b(\d{1,3})\s*[\/⁄]\s*(\d{1,3})\b/);
      if (m) {
        const val = parseInt(m[1], 10);
        const max = m[2] ? parseInt(m[2], 10) : 10;
        if (!isNaN(val) && !isNaN(max) && max > 0)
          return { scoreVal: val, scoreMax: max };
      }
      // Pure numeric string -> /10
      m = input.match(/^\s*(\d{1,3})\s*$/);
      if (m) {
        const val = parseInt(m[1], 10);
        if (!isNaN(val)) return { scoreVal: val, scoreMax: 10 };
      }
    }
    return { scoreVal: null, scoreMax: null };
  };

  // Normalizace Markdown nadpisů bez mezery: "##Analýza" -> "## Analýza"
  const normalizeMarkdownHeadings = (text) => {
    if (!text || typeof text !== "string") return text;
    return text.replace(/^(#{1,6})([^#\s])(.*)$/gm, (m, hashes, ch, rest) => {
      return `${hashes} ${ch}${rest}`;
    });
  };

  const handleConfirmMagic = async () => {
    if (!activeResult || isMagicSubmitting) return;
    // Block only if a valid analysis already exists for this item (errors don't block retry)
    const existingKey = getStableKey(activeResult, activeIndex);
    const existingCandidate =
      aiMessagesByKey[existingKey] ||
      activeResult?.analysis_result ||
      loadAnalysisFromStorage(existingKey)?.payload ||
      loadAnalysisFromStorage(existingKey)?.markdown;
    const existingParsed = extractPayload(existingCandidate);
    if (isValidAnalysisPayload(existingParsed)) {
      setIsMagicOpen(false);
      return;
    }
    // Credit pre-check (same approach as ImageDropzone): require at least 1 credit
    const currentCredits = Math.max(
      0,
      Number(userData?.available_credits ?? 0),
    );
    if (currentCredits <= 0) {
      setIsMagicOpen(false);
      if (typeof setShowModalCredit === "function") setShowModalCredit(true);
      return;
    }
    const prompt = PROMPT_MAP[magicType] || "";
    // Backend vyžaduje heatmap_id v cestě (fallback na image_id, pokud by někdy chyběl)
    const analysisId = activeResult.heatmap_id || activeResult.image_id;
    if (!analysisId) {
      console.error("Chybí ID pro analyze_heatmaps.");
      setIsMagicOpen(false);
      return;
    }
    setIsMagicSubmitting(true);
    try {
      const { ok, payload } = await requestAnalysisWithRetry(
        analysisId,
        prompt,
        10,
      );
      const key = getStableKey(activeResult, activeIndex);
      // On failure, provide a structured error payload so the UI can render a friendly message
      const raw = ok
        ? (payload ?? "Bez odpovědi.")
        : {
            _error: true,
            message: "Při pokusu o AI analýzu došlo k chybě.",
            email: "info@permoniq.com",
          };
      const { scoreVal, scoreMax } = parseScore(raw);
      setAiMessagesByKey((prev) => ({ ...prev, [key]: raw }));
      if (typeof scoreVal === "number" && typeof scoreMax === "number") {
        setAiScoresByKey((prev) => ({
          ...prev,
          [key]: { val: scoreVal, max: scoreMax },
        }));
      }
      // Deduct 1 credit locally after a completed request (mirrors ImageDropzone local deduction)
      setUserData((prev) => {
        const prevCredits = Math.max(
          0,
          Number(prev?.available_credits ?? currentCredits),
        );
        const newCredits = Math.max(0, prevCredits - 1);
        return { ...prev, available_credits: newCredits };
      });
      setActiveTab("heatmap");
      setScrollToAnalysisKey(key);
      saveAnalysisToStorage(key, {
        payload: raw,
        score:
          typeof scoreVal === "number" && typeof scoreMax === "number"
            ? { val: scoreVal, max: scoreMax }
            : null,
      });
      setIsMagicOpen(false);
      if (!ok) {
        console.warn(
          "AI analysis didn't return valid JSON within retry limit.",
        );
      }
      // Notify parent so History view reloads from server rather than cache
      try {
        if (typeof onAiAnalysisCompleted === "function") {
          onAiAnalysisCompleted();
        }
      } catch (e) {
        // ignore
      }
    } finally {
      setIsMagicSubmitting(false);
    }
  };

  return (
    <div className={styles.viewerContainer}>
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <h3 className={styles.sidebarTitle}>Právě analyzováno</h3>
        </div>
        <ul className={styles.thumbnailList}>
          {analysisData.map((result, index) => {
            // Vytvoříme stabilní klíč kombinací image_id a hash obsahu
            const stableKey = `${result.image_id}_${
              result.original_image_base64?.substring(0, 10) || index
            }`;

            return (
              <li key={stableKey} title={result.image_id}>
                <button
                  className={`${styles.thumbnailItem} ${
                    index === activeIndex ? styles.active : ""
                  }`}
                  onClick={() => handleSelectImage(index)}
                >
                  <img
                    src={`data:image/png;base64,${result.original_image_base64}`}
                    alt={`Náhled ${result.image_id}`}
                    className={styles.thumbnailImage}
                  />
                  <span className={styles.thumbnailName}>
                    {result.image_id}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          className={`${styles.tabButton} ${styles.iconTabButton} ${styles.saveTabButton} ${styles.bulkDownloadFloatingBtn}`}
          onClick={handleBulkDownload}
          title="Stáhnout všechny heatmapy"
          aria-label="Stáhnout všechny heatmapy"
        >
          <ReactSVG src={downloadIcon} />
        </button>
      </div>
      <div className={styles.mainContent}>
        <h2 className={styles.mainTitle}>{activeResult.image_id}</h2>
        <div className={styles.tabs}>
          {Object.entries(IMAGE_TYPES).map(([key, def]) => (
            <button
              key={key}
              className={`${styles.tabButton} ${
                activeTab === key ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab(key)}
            >
              {def.label}
            </button>
          ))}
          {(() => {
            const key = getStableKey(activeResult, activeIndex);
            let stored = aiScoresByKey[key];
            if (
              stored &&
              typeof stored.val === "number" &&
              typeof stored.max === "number"
            ) {
              return (
                <div className={styles.scoreBadge} aria-label="Skóre banneru">
                  <span className={styles.scoreLabel}>Skóre:</span>
                  <span className={styles.scoreValue}>{stored.val}</span>
                  <span className={styles.scoreSlash}>/</span>
                  <span className={styles.scoreTotal}>{stored.max}</span>
                </div>
              );
            }
            // Fallback: parse score from available sources (string or JSON)
            const rawAny =
              aiMessagesByKey[key] ||
              activeResult?.analysis_result ||
              loadAnalysisFromStorage(key)?.payload ||
              loadAnalysisFromStorage(key)?.markdown ||
              "";
            const { scoreVal, scoreMax } = rawAny
              ? parseScore(rawAny)
              : { scoreVal: null, scoreMax: null };
            if (typeof scoreVal !== "number" || typeof scoreMax !== "number")
              return null;
            return (
              <div className={styles.scoreBadge} aria-label="Skóre banneru">
                <span className={styles.scoreLabel}>Skóre:</span>
                <span className={styles.scoreValue}>{scoreVal}</span>
                <span className={styles.scoreSlash}>/</span>
                <span className={styles.scoreTotal}>{scoreMax}</span>
              </div>
            );
          })()}
          {(() => {
            // Place Save button to the right; if there's no score badge, push it with margin-left:auto
            const key = getStableKey(activeResult, activeIndex);
            let hasScore = false;
            const stored = aiScoresByKey[key];
            if (
              stored &&
              typeof stored.val === "number" &&
              typeof stored.max === "number"
            ) {
              hasScore = true;
            } else {
              const rawAny =
                aiMessagesByKey[key] ||
                activeResult?.analysis_result ||
                loadAnalysisFromStorage(key)?.payload ||
                loadAnalysisFromStorage(key)?.markdown ||
                "";
              const { scoreVal, scoreMax } = rawAny
                ? parseScore(rawAny)
                : { scoreVal: null, scoreMax: null };
              hasScore =
                typeof scoreVal === "number" && typeof scoreMax === "number";
            }
            const saveTitle = `Uložit ${
              IMAGE_TYPES[activeTab]?.button || "obrázek"
            }`;
            return (
              <button
                type="button"
                className={`${styles.tabButton} ${styles.iconTabButton} ${
                  styles.saveTabButton
                } ${!hasScore ? styles.rightAlign : ""}`}
                title={saveTitle}
                aria-label={saveTitle}
                onClick={handleDownload}
              >
                <ReactSVG src={downloadIcon} />
              </button>
            );
          })()}
          {/* AI tlačítko přesunuto do footeru */}
        </div>
        <div className={styles.imageContainer}>
          <img
            src={getActiveImageData()}
            alt={`Zobrazení: ${IMAGE_TYPES[activeTab]?.label || ""}`}
            className={styles.resultImage}
            onClick={handleOpenPreview}
            role="button"
          />
        </div>
        {activeTab === "heatmap" &&
          Object.keys(aiMessagesByKey).length === 0 && (
            <HintBox
              text={
                "Na zvýrazněné oblasti heatmapy se podle neuromarketingových studií zaměří první pohled více než 90 % uživatelů."
              }
            />
          )}
        {(() => {
          // Zobrazit odpověď pod heatmapou
          if (activeTab !== "heatmap") return null;
          const key = getStableKey(activeResult, activeIndex);
          const msgAny =
            aiMessagesByKey[key] ||
            activeResult?.analysis_result ||
            loadAnalysisFromStorage(key)?.payload ||
            loadAnalysisFromStorage(key)?.markdown;
          if (!msgAny) return null;
          return (
            <div
              className={styles.aiMessageBox}
              ref={analysisRef}
              style={{ scrollMarginTop: 50 }}
            >
              {renderAnalysisContent(extractPayload(msgAny))}
            </div>
          );
        })()}
        <div className={styles.footer}>
          {(() => {
            const key = getStableKey(activeResult, activeIndex);
            // Show CTA unless a valid analysis payload exists (errors still allow retry)
            const candidate =
              aiMessagesByKey[key] ||
              activeResult?.analysis_result ||
              loadAnalysisFromStorage(key)?.payload ||
              loadAnalysisFromStorage(key)?.markdown;
            const parsed = extractPayload(candidate);
            const hasValidAnalysis = isValidAnalysisPayload(parsed);
            return hasValidAnalysis ? null : (
              <ButtonBtn
                className={styles.aiCtaFooterBtn}
                marginBlock="0"
                justifyContent="flex-start"
                onClick={() => setIsMagicOpen(true)}
                title="AI analýza"
                aria-label="AI analýza"
              >
                <ReactSVG src={wandIcon} />
                <span className={styles.iconText}>Zpracuj AI analýzu</span>
              </ButtonBtn>
            );
          })()}
        </div>
      </div>
      {isMagicOpen && (
        <AIPurposeModal
          selectedType={magicType}
          onChangeType={setMagicType}
          onConfirm={handleConfirmMagic}
          onClose={() => setIsMagicOpen(false)}
          isSubmitting={isMagicSubmitting}
        />
      )}
      {isPreviewOpen && (
        <HeatmapPreviewModal
          onClose={() => setIsPreviewOpen(false)}
          imageSrc={getActiveImageData()}
          title={activeResult?.image_id}
          bgOpacity={1}
          analysisPayload={(() => {
            const key = getStableKey(activeResult, activeIndex);
            const candidate =
              aiMessagesByKey[key] ||
              activeResult?.analysis_result ||
              loadAnalysisFromStorage(key)?.payload ||
              loadAnalysisFromStorage(key)?.markdown;
            const parsed = candidate ? extractPayload(candidate) : null;
            return isValidAnalysisPayload(parsed) ? parsed : null;
          })()}
          score={(() => {
            const key = getStableKey(activeResult, activeIndex);
            const stored = aiScoresByKey[key];
            if (
              stored &&
              typeof stored.val === "number" &&
              typeof stored.max === "number"
            )
              return stored;
            const rawAny =
              aiMessagesByKey[key] ||
              activeResult?.analysis_result ||
              loadAnalysisFromStorage(key)?.payload ||
              "";
            const { scoreVal, scoreMax } = rawAny
              ? parseScore(rawAny)
              : { scoreVal: null, scoreMax: null };
            return typeof scoreVal === "number" && typeof scoreMax === "number"
              ? { val: scoreVal, max: scoreMax }
              : null;
          })()}
        />
      )}
      {isMagicSubmitting && <ModalLoading message="Probíhá analýza" />}
    </div>
  );
};

export default AnalysisViewer;
